/**
 * 🚗 VEHICLE APPROVAL SERVICE - Integration with vehicle registration APIs
 * Handles vehicle pending approvals and approve/reject actions
 */

// API Base URL
import { getApiBaseUrl } from '@/config/environment';


export interface VehicleInfo {
  make: string;
  model: string;
  year: string;
  type?: string; // Legacy field - kept for backward compatibility
  color: string;
  plateNumber: string;
  state?: string;
  city?: string;
  country?: string;
  transmission: string;
  fuelType: string;
  seatingCapacity: number;
  marketValue: number | null;
  vin?: string | null; // VIN field
  hasVin?: boolean; // Easy check for VIN presence
  // Vehicle type and body type (from backend)
  vehicleTypeId?: string;
  vehicleTypeName?: string;
  vehicleTypeCode?: string;
  bodyTypeId?: string;
  bodyTypeName?: string;
  bodyTypeCode?: string;
}

export interface PricingInfo {
  selfDrive: number | null;
  withDriver: number | null;
  halfDaySelfDrive?: number | null;
  halfDayWithDriver?: number | null;
  fuelPrice?: number | null;
  fuelPriceFullTank?: number | null; // Backend field name
  currency: string; // ISO 4217 code: NGN, KES, GHS, ZAR, USD, EUR, GBP
  rentalOptions?: string[]; // e.g., ['self_drive', 'with_driver']
}

// Currency configuration
export const CURRENCY_CONFIG = {
  NGN: { symbol: '₦', name: 'Nigerian Naira', locale: 'en-NG' },
  KES: { symbol: 'KSh', name: 'Kenyan Shilling', locale: 'en-KE' },
  GHS: { symbol: '₵', name: 'Ghanaian Cedi', locale: 'en-GH' },
  ZAR: { symbol: 'R', name: 'South African Rand', locale: 'en-ZA' },
  USD: { symbol: '$', name: 'US Dollar', locale: 'en-US' },
  EUR: { symbol: '€', name: 'Euro', locale: 'en-EU' },
  GBP: { symbol: '£', name: 'British Pound', locale: 'en-GB' },
} as const;

export type CurrencyCode = keyof typeof CURRENCY_CONFIG;

export interface InsuranceInfo {
  type: string;
  paymentTerm: string;
  hasGPS: boolean;
  hasInspectionCertificate: boolean;
}

export interface HostInfo {
  id: string;
  name: string;
  email: string | null;
  phone: string;
  profilePicture: string | null;
}

export interface PhotoInfo {
  id: string;
  category: string;
  isPrimary: boolean;
}

export interface ProofOfOwnershipPhoto {
  id: string;
  url: string;
  type: 'registration' | 'proof_of_ownership' | 'insurance';
  uploadedAt: string;
}

export interface ProofOfOwnershipInfo {
  count: number;
  items: ProofOfOwnershipPhoto[];
  required: boolean; // True when VIN is missing
  types: string[]; // Available types: registration, proof_of_ownership, insurance
}

export interface ReviewInfo {
  reviewedAt: string | null;
  reviewedBy: string | null;
  adminNotes: string | null;
  rejectionReason: string | null;
}

export interface PendingVehicle {
  id: string;
  status: string;
  vehicle: VehicleInfo;
  insurance: InsuranceInfo;
  host: HostInfo;
  pricing: PricingInfo;
  photos: {
    count: number;
    items: PhotoInfo[];
  };
  proofOfOwnership?: ProofOfOwnershipInfo; // Separate category for proof of ownership (required when VIN missing)
  instantBooking: boolean;
  review: ReviewInfo;
  createdAt: string;
  updatedAt: string;
  // Moderation fields
  isActive?: boolean; // Visibility to renters
  suspensionStatus?: 'active' | 'suspended' | 'blacklisted';
  suspensionReason?: string;
  suspendedAt?: string;
  canHostReactivate?: boolean;
}

export interface VehicleApprovalsResponse {
  success: boolean;
  data: {
    vehicles: PendingVehicle[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };
  error?: string;
}

export interface VehicleActionResponse {
  success: boolean;
  message?: string;
  data?: {
    vehicleId: string;
    status: string;
    reviewedAt: string;
    reviewedBy: string;
    notes: string;
  };
  error?: string;
}

export const vehicleApprovalService = {

  /**
   * 🚗 Get pending vehicle registrations
   */
  async getPendingVehicles(filters?: {
    status?: 'draft' | 'pending_approval' | 'approved' | 'rejected';
    limit?: number;
    offset?: number;
    fuel_type?: 'petrol' | 'gasoline' | 'diesel' | 'electric' | 'hybrid' | 'all';
    transmission?: 'automatic' | 'manual' | 'all';
    vehicle_type?: string; // sedan, suv, truck, van, etc.
    search?: string; // Search by owner name, email, plate number, make, model, VIN
  }): Promise<VehicleApprovalsResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      if (filters?.fuel_type && filters.fuel_type !== 'all') queryParams.append('fuel_type', filters.fuel_type);
      if (filters?.transmission && filters.transmission !== 'all') queryParams.append('transmission', filters.transmission);
      if (filters?.vehicle_type && filters.vehicle_type !== 'all') queryParams.append('type', filters.vehicle_type);
      if (filters?.search) queryParams.append('search', filters.search);

      const url = `${getApiBaseUrl()}/api/v1/admin/vehicles/pending${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Vehicle API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      // Debug: Log RAW vehicle data from backend BEFORE any mapping
      if (result.data?.vehicles?.length > 0) {
        const rawVehicle = result.data.vehicles[0];
      }

      // Map is_active (snake_case from backend) to isActive (camelCase for frontend)
      if (result.data?.vehicles) {
        result.data.vehicles = result.data.vehicles.map((vehicle: any) => ({
          ...vehicle,
          isActive: vehicle.isActive !== undefined ? vehicle.isActive : vehicle.is_active
        }));
      }

      // Debug: Log first vehicle's full data to verify mapping
      if (result.data?.vehicles?.length > 0) {
        const firstVehicle = result.data.vehicles[0];
      }

      return result;
    } catch (error) {
      console.error('❌ Error fetching pending vehicles:', error);

      return {
        success: false,
        data: {
          vehicles: [],
          pagination: {
            total: 0,
            limit: 50,
            offset: 0,
            hasMore: false
          }
        },
        error: error instanceof Error ? error.message : 'Failed to fetch vehicles'
      };
    }
  },

  /**
   * ✅ Approve vehicle registration
   */
  async approveVehicle(vehicleId: string, adminUserId: string, notes?: string): Promise<VehicleActionResponse> {
    try {
      const url = `${getApiBaseUrl()}/api/v1/admin/vehicles/${vehicleId}/review`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'approve',
          adminUserId,
          notes: notes || 'Vehicle verified and approved via admin dashboard'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Approve Vehicle API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error approving vehicle:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to approve vehicle'
      };
    }
  },

  /**
   * ❌ Reject vehicle registration
   */
  async rejectVehicle(vehicleId: string, adminUserId: string, reason: string): Promise<VehicleActionResponse> {
    try {
      const url = `${getApiBaseUrl()}/api/v1/admin/vehicles/${vehicleId}/review`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'reject',
          adminUserId,
          notes: reason
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Reject Vehicle API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error rejecting vehicle:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reject vehicle'
      };
    }
  },

  /**
   * 🔄 Revert vehicle to pending (unapprove/unreject)
   */
  async revertToPending(vehicleId: string, adminUserId: string, notes?: string): Promise<VehicleActionResponse> {
    try {
      const url = `${getApiBaseUrl()}/api/v1/admin/vehicles/${vehicleId}/review`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'revert_to_pending',
          adminUserId,
          notes: notes || 'Reverting to pending for re-review'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Revert to Pending API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error reverting vehicle to pending:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to revert vehicle to pending'
      };
    }
  },

  /**
   * 💰 Update vehicle pricing
   */
  async updateVehiclePricing(vehicleId: string, pricing: {
    selfDrive?: number | null;
    withDriver?: number | null;
    halfDaySelfDrive?: number | null;
    halfDayWithDriver?: number | null;
    fuelPrice?: number | null;
    currency?: string; // ISO 4217 code, auto-detects if not provided
  }): Promise<{
    success: boolean;
    message?: string;
    data?: any;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl()}/api/v1/admin/vehicles/${vehicleId}/pricing`;

      // Get admin user ID from localStorage
      const getAdminUserId = (): string => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            return user.id || user.userId || 'admin-default';
          } catch (e) {
          }
        }
        return 'admin-default';
      };

      const adminUserId = getAdminUserId();

      // Build request body matching backend format
      const requestBody: any = {
        adminUserId
      };

      // Only include fields that have values
      if (pricing.selfDrive !== null && pricing.selfDrive !== undefined) {
        requestBody.selfDrive = pricing.selfDrive;
      }
      if (pricing.withDriver !== null && pricing.withDriver !== undefined) {
        requestBody.withDriver = pricing.withDriver;
      }
      if (pricing.halfDaySelfDrive !== null && pricing.halfDaySelfDrive !== undefined) {
        requestBody.halfDaySelfDrive = pricing.halfDaySelfDrive;
      }
      if (pricing.halfDayWithDriver !== null && pricing.halfDayWithDriver !== undefined) {
        requestBody.halfDayWithDriver = pricing.halfDayWithDriver;
      }
      if (pricing.fuelPrice !== null && pricing.fuelPrice !== undefined) {
        requestBody.fuelPriceFullTank = pricing.fuelPrice; // Backend expects fuelPriceFullTank
      }
      if (pricing.currency) {
        requestBody.currency = pricing.currency;
      }


      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Update Pricing API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to update pricing (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: result.success !== undefined ? result.success : true,
        message: result.message || 'Pricing updated successfully',
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error updating vehicle pricing:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update pricing'
      };
    }
  },

  /**
   * 🚗 Update vehicle details (plate number, state, city, year, seating capacity, type, body type)
   */
  async updateVehicleDetails(vehicleId: string, details: {
    plateNumber?: string;
    state?: string;
    city?: string;
    country?: string;
    year?: number;
    seatingCapacity?: number;
    vehicleTypeId?: string;
    bodyTypeId?: string;
  }): Promise<{
    success: boolean;
    message?: string;
    data?: any;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl()}/api/v1/admin/vehicles/${vehicleId}/details`;

      // Get admin user ID from localStorage
      const getAdminUserId = (): string => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            return user.id || user.userId || 'admin-default';
          } catch (e) {
          }
        }
        return 'admin-default';
      };

      const adminUserId = getAdminUserId();

      // Build request body
      const requestBody: any = {
        adminUserId,
        ...details
      };


      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Update Vehicle Details API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to update vehicle details (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: result.success !== undefined ? result.success : true,
        message: result.message || 'Vehicle details updated successfully',
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error updating vehicle details:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update vehicle details'
      };
    }
  },

  /**
   * 📊 Get vehicle approval stats (fast, cached counts only)
   * Ultra-fast endpoint that returns just the counts for each status
   */
  async getApprovalStats(filters?: {
    search?: string;
  }): Promise<{
    success: boolean;
    data?: {
      total: number;
      draft: number;
      pending_approval: number;
      approved: number;
      rejected: number;
    };
    _meta?: {
      cached: boolean;
      ttl: number;
    };
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.search) queryParams.append('search', filters.search);

      const url = `${getApiBaseUrl()}/api/v1/admin/vehicles/approval-stats${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Vehicle Stats API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error fetching vehicle approval stats:', error);
      return {
        success: false,
        data: {
          total: 0,
          draft: 0,
          pending_approval: 0,
          approved: 0,
          rejected: 0
        },
        error: error instanceof Error ? error.message : 'Failed to fetch stats'
      };
    }
  }
};
